/**
 * V-11 — Context-aware "hjelp her"-knapp.
 * Viser badge med antall relevante artikler for gjeldende rute.
 * Klikk åpner panel med liste over artiklene.
 */
import { useState, useEffect, useRef } from 'react';
import { Lightbulb, X, Send } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useHelpForRoute } from '../../hooks/useHelpForRoute';
import { submitSuggestion, categoryLabel } from '../../lib/help';
import HelpPanel from './HelpPanel';

export default function ContextHelpButton() {
  const { articles, loading } = useHelpForRoute('nb');
  const location = useLocation();
  const [listOpen, setListOpen] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestText, setSuggestText] = useState('');
  const [suggestStatus, setSuggestStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Lukk ved klikk utenfor
  useEffect(() => {
    if (!listOpen) return;
    const h = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!target.closest('[data-ctx-help]') && !buttonRef.current?.contains(target as Node)) {
        setListOpen(false);
      }
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setListOpen(false); };
    document.addEventListener('mousedown', h);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', h);
      document.removeEventListener('keydown', esc);
    };
  }, [listOpen]);

  const count = articles.length;
  if (loading && count === 0) return null;

  const submitSuggest = async () => {
    if (!suggestText.trim() || suggestText.length < 5) return;
    setSuggestStatus('sending');
    try {
      await submitSuggestion(location.pathname, suggestText.trim());
      setSuggestStatus('sent');
      setSuggestText('');
      setTimeout(() => { setShowSuggest(false); setSuggestStatus('idle'); }, 2000);
    } catch {
      setSuggestStatus('error');
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setListOpen(v => !v)}
        className="relative inline-flex items-center justify-center w-7 h-7 rounded-full bg-kf-orange-500/10 hover:bg-kf-orange-500 hover:text-white text-kf-orange-600 border border-kf-orange-500/30 transition-colors text-sm leading-none"
        aria-label={count > 0 ? `${count} relevante hjelpe-artikler for denne siden` : 'Hjelpe-artikler for denne siden'}
        title={count > 0 ? `${count} artikler for denne siden` : 'Ingen artikler for denne siden'}
      >
        <Lightbulb className="w-4 h-4" />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 bg-kf-orange-500 text-white rounded-full text-[9px] font-bold px-1.5 min-w-[16px] h-4 flex items-center justify-center">
            {count}
          </span>
        )}
      </button>

      {listOpen && createPortal(
        <div
          data-ctx-help
          className="fixed top-16 right-4 w-80 max-w-[90vw] bg-white border border-kf-gray-200 rounded-lg shadow-xl z-[8000] overflow-hidden"
          role="dialog"
          aria-label="Hjelpe-artikler for denne siden"
        >
          <header className="px-4 py-3 border-b border-kf-gray-200 bg-kf-orange-50 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wide text-kf-orange-700 font-semibold">
                Hjelp for denne siden
              </div>
              <div className="text-sm font-semibold text-kf-gray-900">
                {count === 0 ? 'Ingen artikler ennå' : `${count} ${count === 1 ? 'artikkel' : 'artikler'}`}
              </div>
            </div>
            <button onClick={() => setListOpen(false)} className="p-1 text-kf-gray-500 hover:text-kf-gray-900">
              <X className="w-4 h-4" />
            </button>
          </header>

          <div className="max-h-80 overflow-y-auto">
            {count === 0 && !showSuggest && (
              <div className="p-4 text-sm text-kf-gray-600">
                Ingen hjelpe-artikler er registrert for denne siden ennå. Hjelp oss ved å foreslå hva som burde forklares.
              </div>
            )}
            {articles.map(a => (
              <button
                key={a.id}
                onClick={() => { setSelectedSlug(a.slug); setListOpen(false); }}
                className="w-full text-left px-4 py-3 border-b border-kf-gray-100 hover:bg-kf-gray-50 last:border-b-0"
              >
                <div className="text-sm font-semibold text-kf-gray-900">{a.title}</div>
                <div className="text-xs text-kf-gray-600 mt-0.5 line-clamp-2">{a.summary}</div>
                <div className="text-[10px] text-kf-gray-500 mt-1 tracking-wide">{categoryLabel(a.category)}</div>
              </button>
            ))}
          </div>

          <footer className="px-4 py-3 border-t border-kf-gray-200 bg-kf-gray-50">
            {!showSuggest ? (
              <button
                onClick={() => setShowSuggest(true)}
                className="text-xs text-kf-blue-700 hover:underline font-semibold"
              >
                Foreslå hjelpe-tekst for denne siden →
              </button>
            ) : (
              <div>
                <label className="text-xs font-semibold text-kf-gray-700">
                  Hva mangler forklaring?
                </label>
                <textarea
                  value={suggestText}
                  onChange={e => setSuggestText(e.target.value)}
                  rows={3}
                  placeholder="F.eks. 'Jeg forstår ikke hva feltet X betyr' eller 'En wizard for første gang ville hjulpet'"
                  className="w-full mt-1 px-2 py-1 border border-kf-gray-300 rounded text-xs"
                />
                <div className="flex items-center justify-between mt-2 gap-2">
                  <button
                    onClick={() => { setShowSuggest(false); setSuggestText(''); }}
                    className="text-xs text-kf-gray-600 hover:text-kf-gray-900"
                  >
                    Avbryt
                  </button>
                  <button
                    onClick={submitSuggest}
                    disabled={suggestText.length < 5 || suggestStatus === 'sending'}
                    className="flex items-center gap-1 px-3 py-1 text-xs bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded disabled:opacity-50"
                  >
                    <Send className="w-3 h-3" />
                    {suggestStatus === 'sending' ? 'Sender...' : suggestStatus === 'sent' ? '✓ Sendt' : 'Send'}
                  </button>
                </div>
                {suggestStatus === 'sent' && (
                  <div className="text-xs text-emerald-700 mt-1">
                    Takk — Kompetanseforbundet gjennomgår forslaget.
                  </div>
                )}
                {suggestStatus === 'error' && (
                  <div className="text-xs text-red-700 mt-1">
                    Kunne ikke sende. Prøv igjen senere.
                  </div>
                )}
              </div>
            )}
          </footer>
        </div>,
        document.body
      )}

      {selectedSlug && (
        <HelpPanel
          slug={selectedSlug}
          isOpen={!!selectedSlug}
          onClose={() => setSelectedSlug(null)}
          route={location.pathname}
        />
      )}
    </>
  );
}
